import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';
import {
  runEvaluation as runEvaluationService,
  deleteEvaluation as deleteEvaluationService,
} from '../services/api';
import type { Evaluation, EvaluationInput } from '../types';

/**
 * Hook to fetch evaluations for a project
 * Uses real-time Firestore listener
 */
export const useEvaluations = (projectId: string | null) => {
  const { user } = useAuth();
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user || !projectId) {
      setEvaluations([]);
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'evaluations'),
        where('projectId', '==', projectId),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const evals = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              evaluationId: doc.id,
              projectId: data.projectId,
              title: data.title,
              assetDescription: data.assetDescription,
              assetUrl: data.assetUrl,
              personaIds: data.personaIds,
              reportUrl: data.reportUrl,
              reportMd: data.reportMd,
              status: data.status,
              createdAt: data.createdAt?.toDate() || new Date(),
              completedAt: data.completedAt?.toDate(),
            } as Evaluation;
          });

          setEvaluations(evals);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching evaluations:', err);
          setError(err as Error);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up evaluations listener:', err);
      setError(err as Error);
      setLoading(false);
    }
  }, [user, projectId]);

  return { evaluations, loading, error };
};

/**
 * Hook to run an evaluation
 */
export const useRunEvaluation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const runEvaluation = useCallback(async (input: EvaluationInput) => {
    setLoading(true);
    setError(null);

    try {
      const result = await runEvaluationService(input);
      setLoading(false);
      return result;
    } catch (err) {
      console.error('Error running evaluation:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to run evaluation';
      setError(new Error(errorMessage));
      setLoading(false);
      throw err;
    }
  }, []);

  return { runEvaluation, loading, error };
};

/**
 * Hook to delete an evaluation
 */
export const useDeleteEvaluation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteEvaluation = useCallback(async (evaluationId: string) => {
    setLoading(true);
    setError(null);

    try {
      await deleteEvaluationService(evaluationId);
      setLoading(false);
    } catch (err) {
      console.error('Error deleting evaluation:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete evaluation';
      setError(new Error(errorMessage));
      setLoading(false);
      throw err;
    }
  }, []);

  return { deleteEvaluation, loading, error };
};
